import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import Container from 'web/components/Container';
import { LinkStyled } from 'web/components/elements';
import Meta from 'web/components/Meta';
import useErrorHandler from 'web/hooks/useErrorHandler';
import useInitialRouteData from 'web/hooks/useInitialRouteData';
import useQueryCache from 'web/hooks/useQueryCache';
import useSanityQuery from 'web/hooks/useSanityQuery';
import { FakeText, SubTitle, Title } from './common';
import PostsList, { PostsListLoading } from './PostsList';
import { Category, Post } from './types';

const blogCategoryQuery = `*[_type == "blogCategory" && slug.current == $slug][0] {
  _id,
  title,
  description,
  "posts": *[_type == "blogPost" && references(^._id)] | order(publishedAt desc) {
    _id,
    slug,
    title,
    description,
    publishedAt,
    "coverImage": {
      "_type": coverImage._type,
      "alt": coverImage.alt,
      "asset": coverImage.asset,
      "metadata": coverImage.asset->metadata{
          lqip
        }
    },
    "readTime": round(length(pt::text(body)) / 5 / 180)
  }
}`;

const BlogCategory = () => {
  const { slug } = useParams();
  const sanityParams = useMemo(() => ({ slug }), [slug]);
  const initialData = useInitialRouteData<Category & { posts: Post[] }>();
  const result = useSanityQuery<Category & { posts: Post[] }>(blogCategoryQuery, sanityParams);
  const [category, loading, error] = useQueryCache(`blog:category:${slug}`, result, initialData);
  useErrorHandler(error);

  return (
    <Container style={{ marginBottom: 80 }}>
      <nav>
        <LinkStyled to="/blog">Introwise Blog</LinkStyled>
      </nav>
      {loading && (
        <>
          <FakeText as="h1">Loading...</FakeText>
          <FakeText as="p">Loading...</FakeText>
          <PostsListLoading />
        </>
      )}
      {!loading && error && <>{`${error}`}</>}
      {!loading && !error && !category && (
        <>
          <Meta title={`Page not found – Introwise Blog`} />
          <Title>Not found</Title>
          <SubTitle>
            The page you are looking for doesn&apos;t exist. But you might find something interesting in{' '}
            <LinkStyled to="/blog">our blog</LinkStyled>.
          </SubTitle>
        </>
      )}
      {!loading && !error && category && (
        <>
          <Meta title={`${category.title} – Introwise Blog`} description={category.description} />
          <Title>{category.title}</Title>
          <SubTitle>{category.description}</SubTitle>
          <PostsList posts={category.posts} />
        </>
      )}
    </Container>
  );
};

export default BlogCategory;
